import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { X, CheckCircle, AlertCircle } from "lucide-react";
import { MmkCategory } from "../../types/mmk";
import {
  mmkBackToMmkButtonStyles,
  mmkQuizSummaryCardStyles,
} from "./styles";
import { MmkPageShell } from "./mmk-page-shell";

interface CategoryBreakdown {
  correct: number;
  wrong: number;
}

interface QuizSummaryProps {
  score: number;
  total: number;
  categoryBreakdown: Record<string, CategoryBreakdown>;
  onBack: () => void;
}

const MMK_CATEGORIES: MmkCategory[] = [
  "Indication",
  "Dosing",
  "Safety",
  "Efficacy",
  "Patient Selection",
  "Differentiation",
];

export const QuizSummary: React.FC<QuizSummaryProps> = ({
  score,
  total,
  categoryBreakdown,
  onBack,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const percent = total > 0 ? Math.round((score / total) * 100) : 0;

  const strongCategories: string[] = [];
  const weakCategories: string[] = [];
  const categoriesWithCorrect: { cat: string; percent: number }[] = [];

  // Iterate over breakdown keys so we don't miss categories (e.g. API returns different casing)
  const categoriesToCheck = [
    ...MMK_CATEGORIES,
    ...Object.keys(categoryBreakdown).filter(
      (k) => !MMK_CATEGORIES.includes(k as MmkCategory),
    ),
  ];
  categoriesToCheck.forEach((cat) => {
    const data = categoryBreakdown[cat];
    if (!data) return;
    const totalInCat = data.correct + data.wrong;
    if (totalInCat === 0) return;
    const catPercent = (data.correct / totalInCat) * 100;
    if (catPercent === 100 && data.correct > 0) strongCategories.push(cat);
    if (data.correct > 0) categoriesWithCorrect.push({ cat, percent: catPercent });
    if (data.wrong > 0) weakCategories.push(cat);
  });

  // If we have correct answers but no 100% categories, show categories with any correct answers
  if (score > 0 && strongCategories.length === 0 && categoriesWithCorrect.length > 0) {
    categoriesWithCorrect
      .sort((a, b) => b.percent - a.percent)
      .forEach(({ cat }) => strongCategories.push(cat));
  }

  return (
    <MmkPageShell>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          sx={mmkBackToMmkButtonStyles}
          startIcon={<X size={18} />}
        >
          Back to MMM
        </Button>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 3,
        }}
      >
        <Box sx={mmkQuizSummaryCardStyles}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 2,
              color: theme.palette.text.primary,
            }}
          >
            Quiz Complete
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "48px",
                fontWeight: 800,
                color: percent >= 80 ? "#22C55E" : percent >= 50 ? "#F59E0B" : "#EF4444",
              }}
            >
              {score}/{total}
            </Typography>
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                color: theme.palette.text.secondary,
              }}
            >
              ({percent}%)
            </Typography>
          </Box>

          {strongCategories.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <CheckCircle size={14} color="#22C55E" />
                Strong areas
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {strongCategories.map((cat) => (
                  <Typography
                    key={cat}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#22C55E",
                      bgcolor: isDark ? "rgba(34, 197, 94, 0.15)" : "#D1FAE5",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    {cat}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {weakCategories.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <AlertCircle size={14} color="#F59E0B" />
                Knowledge gaps
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {weakCategories.map((cat) => (
                  <Typography
                    key={cat}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#F59E0B",
                      bgcolor: isDark ? "rgba(245, 158, 11, 0.15)" : "#FEF3C7",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                    }}
                  >
                    {cat}
                  </Typography>
                ))}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  mt: 1,
                  fontSize: "13px",
                }}
              >
                These cards will be prioritized in your next Focus Session.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </MmkPageShell>
  );
};
